import MenuItem from "@mui/material/MenuItem";
import { useTenantTranslation } from "hooks/formatters";
import { useState } from "react";
import { IconButton, ListItemIcon, Menu, Typography } from "@mui/material";
import BugReportIcon from "@mui/icons-material/BugReport";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import TableChartIcon from "@mui/icons-material/TableChart";
import { useIsAdmin } from "hooks/settings";
import { useNavigateToDebugSearch } from "hooks/navigation";
import { useSearchId } from "hooks/search";
import { Panel } from "../types";
import { useHotkeys } from "react-hotkeys-hook";
import { hotkeys } from "hooks/hotkeys";

interface Props {
  setShowHistory: (show: boolean) => void;
  setPanel: (panel: Panel) => void;
}

export const SearchActionsButton = ({ setShowHistory, setPanel }: Props) => {
  const { t } = useTenantTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const isAdmin = useIsAdmin();
  const navigate = useNavigateToDebugSearch();
  const searchId = useSearchId();

  const handleToggle = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const openDebugPage = () => {
    if (searchId !== null) {
      navigate(searchId);
    }
    handleClose();
  };

  useHotkeys(hotkeys.debugPage, openDebugPage, { enabled: isAdmin });
  useHotkeys(hotkeys.historyPage, () => {
    setShowHistory(true);
  });
  useHotkeys(hotkeys.constraintsPage, () => {
    setPanel("Constraints");
  });

  return (
    <>
      <IconButton onClick={handleToggle} size="small">
        <MoreVertIcon />
      </IconButton>
      <Menu
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        sx={{ mt: 0.5 }}
        slotProps={{ list: { disablePadding: true } }}
      >
        <MenuItem
          onClick={() => {
            setShowHistory(true);
            handleClose();
          }}
          sx={{ px: 2, py: 1 }}
        >
          <ListItemIcon sx={{ color: "text.primary", mr: -0.5 }}>
            <HistoryRoundedIcon />
          </ListItemIcon>
          <Typography variant="button">{t("history")}</Typography>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setPanel("Constraints");
            handleClose();
          }}
          sx={{ px: 2, py: 1 }}
        >
          <ListItemIcon sx={{ color: "text.primary", mr: -0.5 }}>
            <TableChartIcon />
          </ListItemIcon>
          <Typography variant="button">{t("seeConstraints")}</Typography>
        </MenuItem>
        {isAdmin ? (
          <MenuItem onClick={openDebugPage} sx={{ px: 2, py: 1 }}>
            <ListItemIcon sx={{ color: "text.primary", mr: -0.5 }}>
              <BugReportIcon />
            </ListItemIcon>
            <Typography variant="button">{t("debug")}</Typography>
          </MenuItem>
        ) : null}
      </Menu>
    </>
  );
};
